import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Monitor, Plus, Trash2, Pencil, Play, Download, X, Copy, Check,
  ShieldCheck, ClipboardList, KeyRound, Terminal, Sparkles, Mail, Loader2, Power,
  AlertTriangle, Eye, EyeOff, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { dapi as api } from "./deskApi";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const RUSTDESK_DOWNLOAD = "https://rustdesk.com/download";
const ENROLL_URL = `${process.env.REACT_APP_BACKEND_URL}/api/desktop/remote-enroll`;
const HEARTBEAT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/desktop/remote-heartbeat`;
export const REMOTE_SETUP_VERSION = "2026.08.24.4";

const timeAgo = (iso) => {
  if (!iso) return "never seen";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

// Keep the browser launch URI deliberately simple. It is the most broadly
// supported RustDesk form and avoids exposing the permanent password in browser
// history or protocol-handler logs. Connect copies the password instead.
export const connectUri = (c) => `rustdesk://${encodeURIComponent(String(c?.rustdesk_id || "").trim())}`;

// ---- device detection + per-platform installers --------------------------
export const APP_STORE_URL = "https://apps.apple.com/app/rustdesk-remote-desktop/id1581225015";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.carriez.flutter_hbb";
export const RD_DL = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/desktop/rustdesk-download?platform=${p}`;

export const getPlatform = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Macintosh|Mac OS X/.test(ua)) return "mac";
  if (/Windows/.test(ua)) return "windows";
  return "other";
};
const PLATFORM_LABEL = {
  android: "an Android device",
  ios: "an iPhone / iPad",
  mac: "a Mac",
  windows: "a Windows PC",
  other: "this device",
};

// Download without navigating away — an error response must never replace
// the instructions page the user is reading.
export const triggerFileDownload = (url) => {
  const f = document.createElement("iframe");
  f.style.display = "none";
  f.src = url;
  document.body.appendChild(f);
  setTimeout(() => f.remove(), 60000);
};

// One-paste phone setup code: reverse(base64(compact JSON)) — RustDesk's
// official "import server config" format. Pasting it into the app's
// ID-server box fills the server + key fields automatically.
export const buildServerConfigString = (server) => {
  if (!server?.ip || !server?.key) return "";
  const json = JSON.stringify({ host: server.ip, relay: server.ip, api: "", key: server.key });
  return btoa(json).replace(/=+$/, "").split("").reverse().join("");
};

// QR payload the RustDesk app scans (QR icon, top-right): plain-JSON form
// documented for Android — sets ID server + key automatically.
export const serverConfigQr = (server) => {
  if (!server?.ip || !server?.key) return "";
  return `config=${JSON.stringify({ host: server.ip, key: server.key })}`;
};

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let s = "";
  const rnd = crypto.getRandomValues(new Uint32Array(12));
  for (let i = 0; i < 12; i++) s += chars[rnd[i] % chars.length];
  return s;
};

// PowerShell lines that write RustDesk2.toml pointing this PC at the owner's
// PRIVATE RustDesk server (ID + relay + key), to every profile the client/service
// might read. Returns [] when no private server is configured (uses public server).
const serverConfigPsLines = (server) => {
  if (!server || !server.ip || !server.key) return [];
  const ip = String(server.ip).replace(/'/g, "''");
  const key = String(server.key).replace(/'/g, "''");
  return [
    '  Write-Host "Pointing RustDesk at your private server..."',
    "  $toml=\"[options]`ncustom-rendezvous-server = '" + ip + "'`nrelay-server = '" + ip + "'`nkey = '" + key + "'`nallow-auto-update = 'N'`n\"",
    "  $utf8=New-Object System.Text.UTF8Encoding($false)",
    "  $cfgDirs=@(\"$env:APPDATA\\RustDesk\\config\",\"$env:LOCALAPPDATA\\RustDesk\\config\",'C:\\ProgramData\\RustDesk\\config','C:\\Windows\\ServiceProfiles\\LocalService\\AppData\\Roaming\\RustDesk\\config','C:\\Windows\\System32\\config\\systemprofile\\AppData\\Roaming\\RustDesk\\config','C:\\Windows\\SysWOW64\\config\\systemprofile\\AppData\\Roaming\\RustDesk\\config')",
    "  try{ Get-ChildItem 'C:\\Users' -Directory -ErrorAction SilentlyContinue | ForEach-Object { $cfgDirs+=(Join-Path $_.FullName 'AppData\\Roaming\\RustDesk\\config'); $cfgDirs+=(Join-Path $_.FullName 'AppData\\Local\\RustDesk\\config') } }catch{}",
    "  $cfgWritten=0",
    "  foreach($cd in ($cfgDirs | Select-Object -Unique)){ try{ New-Item -ItemType Directory -Force -Path $cd | Out-Null; [IO.File]::WriteAllText((Join-Path $cd 'RustDesk2.toml'),$toml,$utf8); $cfgWritten++ }catch{} }",
    "  if($cfgWritten -lt 1){ throw 'Windows blocked the RustDesk server settings. Run this setup as administrator and try again.' }",
    '  Write-Host "RustDesk settings written to $cfgWritten Windows profiles."',
  ];
};

const serverPreflightPsLines = (server) => {
  if (!server || !server.ip) return [];
  const raw = String(server.ip).trim().replace(/^https?:\/\//i, "").replace(/\/$/, "");
  const ipv4WithPort = raw.match(/^(.+):(\d+)$/);
  const host = (ipv4WithPort ? ipv4WithPort[1] : raw).replace(/'/g, "''");
  const port = ipv4WithPort ? Number(ipv4WithPort[2]) : 21116;
  return [
    `  $rdServerHost='${host}'`,
    `  $rdServerPort=${port}`,
    '  Write-Host "Checking RustDesk server $rdServerHost`:$rdServerPort..."',
    "  $tcp=New-Object Net.Sockets.TcpClient",
    "  try{ $ar=$tcp.BeginConnect($rdServerHost,$rdServerPort,$null,$null); if(-not $ar.AsyncWaitHandle.WaitOne(7000)){ throw 'timeout' }; $tcp.EndConnect($ar) }catch{ throw \"This PC cannot reach the RustDesk server $rdServerHost on TCP $rdServerPort. Allow outbound TCP and UDP $rdServerPort in the firewall/router, then run setup again.\" }finally{ try{ $tcp.Close() }catch{} }",
    '  Write-Host "RustDesk server is reachable." -ForegroundColor Green',
  ];
};

// One-paste PowerShell: silent-install RustDesk, configure its documented Windows
// service profiles, set permanent unattended access, enroll the PC, and install a
// clearly named status agent for Online/Offline + Wake-on-LAN reporting.

export const buildSetupScript = (pw, token, server) => [
  "$ErrorActionPreference='Stop'",
  "$ProgressPreference='SilentlyContinue'",
  "try {",
  `  Write-Host "Remote setup version ${REMOTE_SETUP_VERSION}" -ForegroundColor Cyan`,
  "  [Net.ServicePointManager]::SecurityProtocol=[Net.SecurityProtocolType]::Tls12",
  `  $pw='${(pw || "").replace(/'/g, "''")}'`,
  `  $token='${(token || "").replace(/'/g, "''")}'`,
  `  $enroll='${ENROLL_URL}'`,
  '  $rd="$env:ProgramFiles\\RustDesk\\rustdesk.exe"',
  "  if(-not (Test-Path $rd)){ $rd=\"${env:ProgramFiles(x86)}\\RustDesk\\rustdesk.exe\" }",
  "  $reinstall=$false",
  "  if(Test-Path $rd){",
  '    Write-Host ""',
  '    Write-Host "RustDesk is ALREADY installed on this computer." -ForegroundColor Yellow',
  "    $ans=Read-Host 'Type Y to reinstall the newest version fresh, or just press ENTER to keep it'",
  "    if($ans -match '^[Yy]'){ $reinstall=$true }",
  "  }",
  "  if((Test-Path $rd) -and (-not $reinstall)){",
  '    Write-Host "Keeping the existing RustDesk - skipping download."',
  "  } else {",
  "    if($reinstall){ Write-Host 'Reinstalling RustDesk fresh...' }",
  "    Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue",
  "    try{ Stop-Service 'RustDesk' -Force -ErrorAction SilentlyContinue }catch{}",
  "    Start-Sleep -Seconds 2",
  '    $tmp="$env:TEMP\\rustdesk-setup.exe"',
  '    Write-Host "Downloading RustDesk..."',
  "    $rel=Invoke-RestMethod 'https://api.github.com/repos/rustdesk/rustdesk/releases/latest' -Headers @{ 'User-Agent'='ps' }",
  "    $url=($rel.assets | Where-Object { $_.name -like '*x86_64.exe' } | Select-Object -First 1).browser_download_url",
  "    Invoke-WebRequest $url -OutFile $tmp",
  "    Unblock-File $tmp -ErrorAction SilentlyContinue",
  '    Write-Host "Installing RustDesk..."',
  "    $p=Start-Process $tmp -ArgumentList '--silent-install' -PassThru",
  "    try{ $p | Wait-Process -Timeout 180 -ErrorAction Stop }catch{ Write-Host 'Installer is taking a while, continuing...' }",
  '    $rd="$env:ProgramFiles\\RustDesk\\rustdesk.exe"',
  "    if(-not (Test-Path $rd)){ $rd=\"${env:ProgramFiles(x86)}\\RustDesk\\rustdesk.exe\" }",
  "    for($i=0;$i -lt 20 -and -not (Test-Path $rd);$i++){ Start-Sleep -Seconds 3; if(-not (Test-Path $rd)){ $rd=\"$env:ProgramFiles\\RustDesk\\rustdesk.exe\" } }",
  "  }",
  "  if(-not (Test-Path $rd)){ throw 'RustDesk did not install. Close RustDesk if it is open, then run this file again.' }",
  '  Write-Host "Enabling one-click Connect (registering rustdesk:// link)..."',
  "  try{",
  "    New-Item -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk\\shell\\open\\command' -Force | Out-Null",
  "    Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk' -Name '(default)' -Value 'URL:RustDesk Protocol'",
  "    Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk' -Name 'URL Protocol' -Value ''",
  "    Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk\\shell\\open\\command' -Name '(default)' -Value ('\"' + $rd + '\" \"%1\"')",
  "  }catch{}",
  '  Write-Host "Installing and starting the RustDesk service..."',
  "  try{ Start-Process $rd -ArgumentList '--install-service' -WindowStyle Hidden -ErrorAction SilentlyContinue | Out-Null }catch{}",
  "  Start-Sleep -Seconds 10",
  "  try{ Set-Service -Name 'RustDesk' -StartupType Automatic -ErrorAction SilentlyContinue; Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 8",
  '  Write-Host "Applying the private-server settings to every Windows profile..."',
  "  try{ Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue }catch{}",
  "  try{ Stop-Service 'RustDesk' -Force -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 3",
  ...serverPreflightPsLines(server),
  ...serverConfigPsLines(server),
  "  try{ Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 15",
  "  $svc=Get-Service -Name 'RustDesk' -ErrorAction SilentlyContinue",
  "  if(-not ($svc -and $svc.Status -eq 'Running')){ throw 'RustDesk installed, but its Windows service did not start. Restart Windows and run setup again.' }",
  '  Write-Host "Setting the permanent password..."',
  "  try{ & $rd --password $pw | Out-Null }catch{}",
  "  Start-Sleep -Seconds 3",
  "  try{ & $rd --option verification-method use-permanent-password | Out-Null }catch{}",
  "  Start-Sleep -Seconds 2",
  "  try{ & $rd --option approve-mode password | Out-Null }catch{}",
  "  Start-Sleep -Seconds 3",
  '  Write-Host "Initializing RustDesk once so it creates its device ID..."',
  "  try{ Start-Process $rd -WindowStyle Minimized -ErrorAction SilentlyContinue | Out-Null }catch{}",
  "  Start-Sleep -Seconds 15",
  '  Write-Host "Reading RustDesk ID..."',
  "  $id=''",
  "  $paths=@(\"$env:APPDATA\\RustDesk\\config\\RustDesk.toml\",\"$env:LOCALAPPDATA\\RustDesk\\config\\RustDesk.toml\",'C:\\ProgramData\\RustDesk\\config\\RustDesk.toml','C:\\Windows\\ServiceProfiles\\LocalService\\AppData\\Roaming\\RustDesk\\config\\RustDesk.toml','C:\\Windows\\System32\\config\\systemprofile\\AppData\\Roaming\\RustDesk\\config\\RustDesk.toml','C:\\Windows\\SysWOW64\\config\\systemprofile\\AppData\\Roaming\\RustDesk\\config\\RustDesk.toml')",
  "  try{ Get-ChildItem 'C:\\Users' -Directory -ErrorAction SilentlyContinue | ForEach-Object { $paths+=(Join-Path $_.FullName 'AppData\\Roaming\\RustDesk\\config\\RustDesk.toml') } }catch{}",
  "  for($i=0;$i -lt 12 -and -not $id;$i++){",
  "    foreach($cfg in $paths){ try{ if(Test-Path $cfg){ foreach($ln in (Get-Content $cfg -ErrorAction SilentlyContinue)){ if($ln -match '^\\s*id\\s*=.*?(\\d{6,20})'){ $id=$Matches[1]; break } } } }catch{} if($id){ break } }",
  "    if(-not $id){ try{ $raw=(& $rd --get-id 2>$null | Out-String); if($raw -match '(?<!\\d)(\\d{6,20})(?!\\d)'){ $id=$Matches[1] } }catch{} }",
  "    if(-not $id){ try{ $clip=(Get-Clipboard -Raw -ErrorAction SilentlyContinue); if($clip -match '(?<!\\d)(\\d{6,20})(?!\\d)'){ $id=$Matches[1] } }catch{} }",
  "    if(-not $id){ Start-Sleep -Seconds 5 }",
  "  }",
  "  if(-not $id){ throw \"RustDesk service is running and the server is reachable, but no device ID was created. Restart Windows once, then run this same setup file again.\" }",
  '  Write-Host "RustDesk ID: $id"',
  '  Write-Host "Checking network card + enabling wake-on-LAN..."',
  "  $mac=''; $lip=''",
  "  try{ $ad=Get-NetAdapter -Physical -ErrorAction SilentlyContinue | Where-Object { $_.Status -eq 'Up' } | Select-Object -First 1; if($ad){ $mac=$ad.MacAddress; $ipc=Get-NetIPAddress -InterfaceIndex $ad.ifIndex -AddressFamily IPv4 -ErrorAction SilentlyContinue | Where-Object { $_.IPAddress -notlike '169.*' } | Select-Object -First 1; if($ipc){ $lip=$ipc.IPAddress } } }catch{}",
  "  try{ Get-NetAdapter -Physical -ErrorAction SilentlyContinue | Where-Object { $_.Status -eq 'Up' } | ForEach-Object { try{ Set-NetAdapterPowerManagement -Name $_.Name -WakeOnMagicPacket Enabled -ErrorAction SilentlyContinue }catch{}; try{ Set-NetAdapterAdvancedProperty -Name $_.Name -RegistryKeyword '*WakeOnMagicPacket' -RegistryValue 1 -ErrorAction SilentlyContinue }catch{}; try{ powercfg /deviceenablewake ($_.InterfaceDescription) 2>$null }catch{} } }catch{}",
  '  Write-Host "Adding this computer to your app..."',
  "  $body=@{ token=$token; rustdesk_id=$id; hostname=$env:COMPUTERNAME; mac=$mac; local_ip=$lip } | ConvertTo-Json",
  "  $resp=Invoke-RestMethod -Method Post -Uri $enroll -Body $body -ContentType 'application/json'",
  "  $hb=$resp.hb_token",
  '  $info="RustDesk ID: $id`r`nPermanent password: $pw`r`nUnattended access: ON (no Accept prompt)"',
  "  $info | Out-File \"$([Environment]::GetFolderPath('Desktop'))\\RustDesk-Login.txt\" -Encoding utf8",
  '  Write-Host "Setting up online-status reporting + wake-up..."',
  "  try{",
  "    Set-Service -Name 'RustDesk' -StartupType Automatic -ErrorAction SilentlyContinue",
  "    $agentDir=\"$env:ProgramData\\CatskillsPoolRemote\"",
  "    New-Item -ItemType Directory -Force -Path $agentDir | Out-Null",
  "    $agentFile=Join-Path $agentDir 'status-agent.ps1'",
  "    $agent=@'",
  "# Catskills Pool Supplies - Remote Support status agent.",
  "# Runs every few minutes to report this PC's online status to the app and to",
  "# relay wake-up requests to other office PCs on the same local network.",
  "$ErrorActionPreference='SilentlyContinue'",
  "try{ if((Get-Service RustDesk).Status -ne 'Running'){ Start-Service RustDesk } }catch{}",
  "$hb='__HB__'",
  "$url='__URL__'",
  "$mac='__MAC__'",
  "$lip='__LIP__'",
  "try{",
  "  $body=@{ hb_token=$hb; mac=$mac; local_ip=$lip } | ConvertTo-Json",
  "  $r=Invoke-RestMethod -Method Post -Uri $url -Body $body -ContentType 'application/json' -TimeoutSec 20",
  "  if($r.uninstall){",
  "    try{ Unregister-ScheduledTask -TaskName 'CatskillsPoolRemoteAgent' -Confirm:$false -ErrorAction SilentlyContinue }catch{}",
  "    try{ Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue }catch{}",
  "    Start-Sleep -Seconds 1",
  "    $ex=\"$env:ProgramFiles\\RustDesk\\rustdesk.exe\"; if(-not (Test-Path $ex)){ $ex=\"${env:ProgramFiles(x86)}\\RustDesk\\rustdesk.exe\" }",
  "    $rd=\"$env:ProgramFiles\\RustDesk\\uninstall.exe\"; if(-not (Test-Path $rd)){ $rd=\"${env:ProgramFiles(x86)}\\RustDesk\\uninstall.exe\" }",
  "    try{ if(Test-Path $ex){ Start-Process $ex -ArgumentList '--uninstall-service' -WindowStyle Hidden -ErrorAction SilentlyContinue; Start-Sleep -Seconds 2 } }catch{}",
  "    try{ Stop-Service RustDesk -Force -ErrorAction SilentlyContinue }catch{}",
  "    try{ & sc.exe stop RustDesk | Out-Null }catch{}",
  "    try{ & sc.exe delete RustDesk | Out-Null }catch{}",
  "    try{ if(Test-Path $ex){ Start-Process $ex -ArgumentList '--uninstall' -WindowStyle Hidden -ErrorAction SilentlyContinue } }catch{}",
  "    try{ if(Test-Path $rd){ Start-Process $rd -ArgumentList '/S' -WindowStyle Hidden -Wait -ErrorAction SilentlyContinue } }catch{}",
  "    Start-Sleep -Seconds 2",
  "    try{ Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue }catch{}",
  "    foreach($p in @(\"$env:ProgramFiles\\RustDesk\",\"${env:ProgramFiles(x86)}\\RustDesk\",\"$env:ProgramData\\RustDesk\",\"$env:APPDATA\\RustDesk\",\"$env:LOCALAPPDATA\\RustDesk\")){ try{ if(Test-Path $p){ Remove-Item $p -Recurse -Force -ErrorAction SilentlyContinue } }catch{} }",
  "    try{ Get-ChildItem 'C:\\Users' -Directory -ErrorAction SilentlyContinue | ForEach-Object { foreach($sub in @('AppData\\Roaming\\RustDesk','AppData\\Local\\RustDesk')){ $up=Join-Path $_.FullName $sub; if(Test-Path $up){ Remove-Item $up -Recurse -Force -ErrorAction SilentlyContinue } }; foreach($lf in @('Desktop\\RustDesk.lnk','Desktop\\RustDesk-Login.txt')){ $lp=Join-Path $_.FullName $lf; if(Test-Path $lp){ Remove-Item $lp -Force -ErrorAction SilentlyContinue } } } }catch{}",
  "    try{ foreach($l in @(\"$env:PUBLIC\\Desktop\\RustDesk.lnk\",\"$env:ProgramData\\Microsoft\\Windows\\Start Menu\\Programs\\RustDesk.lnk\",\"$env:ProgramData\\Microsoft\\Windows\\Start Menu\\Programs\\RustDesk\\RustDesk.lnk\")){ if(Test-Path $l){ Remove-Item $l -Recurse -Force -ErrorAction SilentlyContinue } } }catch{}",
  "    try{ $uroots=@('HKLM:\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Uninstall','HKLM:\\SOFTWARE\\WOW6432Node\\Microsoft\\Windows\\CurrentVersion\\Uninstall'); foreach($ur in $uroots){ Get-ChildItem $ur -ErrorAction SilentlyContinue | ForEach-Object { $dn=(Get-ItemProperty $_.PSPath -ErrorAction SilentlyContinue).DisplayName; if($dn -like '*RustDesk*'){ Remove-Item $_.PSPath -Recurse -Force -ErrorAction SilentlyContinue } } } }catch{}",
  "    try{ Get-NetFirewallRule -ErrorAction SilentlyContinue | Where-Object { $_.DisplayName -like '*RustDesk*' } | Remove-NetFirewallRule -ErrorAction SilentlyContinue }catch{}",
  "    try{ Start-Process 'cmd.exe' -ArgumentList ('/c timeout /t 3 >nul & rmdir /s /q \"'+$PSScriptRoot+'\"') -WindowStyle Hidden }catch{}",
  "    return",
  "  }",
  "  if($r.server -and $r.server.ip -and $r.server.key){",
  "    try{",
  "      $hc=if($r.hide_cm){ \"$($r.hide_cm)\" } else { 'N' }",
  "      $ip=\"$($r.server.ip)\"",
  "      $toml=\"[options]`ncustom-rendezvous-server = '$ip'`nrelay-server = '$ip'`nkey = '$($r.server.key)'`nallow-hide-cm = '$hc'`nallow-auto-update = 'N'`n\"",
  "      $utf8=New-Object System.Text.UTF8Encoding($false)",
  "      $svcDirs=@('C:\\Windows\\ServiceProfiles\\LocalService\\AppData\\Roaming\\RustDesk\\config','C:\\Windows\\System32\\config\\systemprofile\\AppData\\Roaming\\RustDesk\\config','C:\\Windows\\SysWOW64\\config\\systemprofile\\AppData\\Roaming\\RustDesk\\config','C:\\ProgramData\\RustDesk\\config')",
  "      $userDirs=@(\"$env:APPDATA\\RustDesk\\config\",\"$env:LOCALAPPDATA\\RustDesk\\config\")",
  "      try{ Get-ChildItem 'C:\\Users' -Directory -ErrorAction SilentlyContinue | ForEach-Object { $userDirs+=(Join-Path $_.FullName 'AppData\\Roaming\\RustDesk\\config'); $userDirs+=(Join-Path $_.FullName 'AppData\\Local\\RustDesk\\config') } }catch{}",
  "      $all=$svcDirs+$userDirs",
  "      $applied=\"$env:ProgramData\\CatskillsPoolRemote\\server.sig\"",
  "      $svcDrift=$false; foreach($d in $svcDirs){ $f=Join-Path $d 'RustDesk2.toml'; if(-not (Test-Path $f)){ $svcDrift=$true }else{ try{ $c=Get-Content $f -Raw -ErrorAction SilentlyContinue; if(-not ($c -and $c.Contains($ip) -and $c.Contains(\"$($r.server.key)\") -and $c.Contains(\"allow-hide-cm = '$hc'\"))){ $svcDrift=$true } }catch{ $svcDrift=$true } } }",
  "      $userDrift=$false; foreach($d in $userDirs){ $f=Join-Path $d 'RustDesk2.toml'; if(-not (Test-Path $f)){ $userDrift=$true }else{ try{ $c=Get-Content $f -Raw -ErrorAction SilentlyContinue; if(-not ($c -and $c.Contains($ip) -and $c.Contains(\"$($r.server.key)\") -and $c.Contains(\"allow-hide-cm = '$hc'\"))){ $userDrift=$true } }catch{ $userDrift=$true } } }",
  "      if(-not (Test-Path $applied)){ $svcDrift=$true }",
  "      if($svcDrift -or $userDrift){",
  "        if($svcDrift){ try{ Stop-Service RustDesk -Force -ErrorAction SilentlyContinue }catch{}; try{ Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue }catch{}; Start-Sleep -Seconds 2 }",
  "        foreach($d in ($all | Select-Object -Unique)){ try{ New-Item -ItemType Directory -Force -Path $d | Out-Null; [IO.File]::WriteAllText((Join-Path $d 'RustDesk2.toml'),$toml,$utf8) }catch{} }",
  "        try{ New-Item -ItemType Directory -Force -Path (Split-Path $applied) | Out-Null; Set-Content -Path $applied -Value \"$ip|$hc\" -Encoding UTF8 -Force }catch{}",
  "        if($svcDrift){ try{ Start-Service RustDesk -ErrorAction SilentlyContinue }catch{} }",
  "      }",
  "    }catch{}",
  "  }",
  "  foreach($m in $r.wake){",
  "    try{",
  "      $mm=($m -replace '[^0-9A-Fa-f]','')",
  "      if($mm.Length -lt 12){ continue }",
  "      $by=0,2,4,6,8,10 | ForEach-Object { [Convert]::ToByte($mm.Substring($_,2),16) }",
  "      $pkt=[byte[]](,0xFF*6) + ($by*16)",
  "      $u=New-Object Net.Sockets.UdpClient",
  "      $u.EnableBroadcast=$true",
  "      $u.Connect([Net.IPAddress]::Broadcast,9)",
  "      [void]$u.Send($pkt,$pkt.Length)",
  "      $u.Close()",
  "    }catch{}",
  "  }",
  "}catch{}",
  "'@",
  "    $agent=$agent.Replace('__HB__',[string]$hb).Replace('__URL__','" + HEARTBEAT_URL + "').Replace('__MAC__',[string]$mac).Replace('__LIP__',[string]$lip)",
  "    Set-Content -Path $agentFile -Value $agent -Encoding UTF8",
  "    $act=New-ScheduledTaskAction -Execute 'powershell.exe' -Argument ('-NoProfile -ExecutionPolicy Bypass -WindowStyle Hidden -File \"'+$agentFile+'\"')",
  "    $trg1=New-ScheduledTaskTrigger -AtStartup",
  "    $trg2=New-ScheduledTaskTrigger -Once -At (Get-Date) -RepetitionInterval (New-TimeSpan -Minutes 3) -RepetitionDuration (New-TimeSpan -Days 3650)",
  "    $pr=New-ScheduledTaskPrincipal -UserId 'SYSTEM' -LogonType ServiceAccount -RunLevel Highest",
  "    Register-ScheduledTask -TaskName 'CatskillsPoolRemoteAgent' -Description 'Reports this PC online status to Catskills Pool Supplies remote support and relays local wake-up requests.' -Action $act -Trigger $trg1,$trg2 -Principal $pr -Force | Out-Null",
  "    Start-ScheduledTask -TaskName 'CatskillsPoolRemoteAgent' -ErrorAction SilentlyContinue",
  "  }catch{}",
  '  Write-Host ""',
  '  Write-Host "SUCCESS - this computer was added to your app."',
  // Keep RustDesk running headless as the Windows service, and make sure NO
  // RustDesk window is ever shown on this PC (the person shouldn't see it pop up).
  "  $svc=Get-Service -Name 'RustDesk' -ErrorAction SilentlyContinue",
  "  if($svc -and $svc.Status -ne 'Running'){ try{ Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}; Start-Sleep -Seconds 3; $svc=Get-Service -Name 'RustDesk' -ErrorAction SilentlyContinue }",
  "  if(-not ($svc -and $svc.Status -eq 'Running')){",
  "    try{ Start-Process $rd -ArgumentList '--install-service' -WindowStyle Hidden -ErrorAction SilentlyContinue; Start-Sleep -Seconds 4; Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  }",
  // Close any visible RustDesk window a few times over ~8s (the installer or
  // service can spawn the UI a moment later) so nothing flashes on screen.
  "  for($k=0;$k -lt 8;$k++){ try{ Get-Process rustdesk -ErrorAction SilentlyContinue | Where-Object { $_.MainWindowHandle -ne 0 } | Stop-Process -Force -ErrorAction SilentlyContinue }catch{}; Start-Sleep -Milliseconds 1000 }",
  "  Start-Sleep -Seconds 1",
  "  exit 0",
  "} catch {",
  "  try{ Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  Write-Host \"\"",
  "  $emsg=$_.Exception.Message",
  "  try{ $rs=$_.Exception.Response.GetResponseStream(); $sr=New-Object IO.StreamReader($rs); $det=$sr.ReadToEnd(); if($det){ try{ $emsg=($det | ConvertFrom-Json).detail }catch{ $emsg=$det } } }catch{}",
  "  Write-Host \"ERROR: $emsg\" -ForegroundColor Red",
  "  Write-Host \"Nothing was sent to your app. Take a photo of this and send it over.\"",
  "  Write-Host \"\"; Write-Host \"Press any key to close...\"",
  "  try{ [void][System.Console]::ReadKey($true) }catch{ Start-Sleep -Seconds 30 }",
  "  exit 1",
  "}",
].join("\n");

// Base64-encode the UTF-8 PowerShell script (so `certutil -decode` writes a clean .ps1).
const toB64Utf8 = (s) => btoa(unescape(encodeURIComponent(s)));

// A self-elevating .bat: prompts for admin (UAC), writes the PS to a .ps1 file via
// certutil, and runs it with -File. Avoids `-EncodedCommand` (blocked by many AVs).
// The base64 is written in small chunks: a single `echo` line longer than ~8191
// chars is silently truncated by cmd.exe, which used to crash the blue window
// instantly before anything ran.
export const buildBat = (ps, successMsg) => {
  const okMsg = successMsg || "This computer is now connected to your app.";
  const b64 = toB64Utf8(ps);
  const CHUNK = 4000; // well under cmd.exe's ~8191-char line limit
  const echoLines = [];
  for (let i = 0; i < b64.length; i += CHUNK) {
    const part = b64.slice(i, i + CHUNK);
    echoLines.push((i === 0 ? ">" : ">>") + "\"%B64%\" echo " + part);
  }
  return [
    "@echo off",
    "title Catskills Pool Supplies - Remote Access Setup",
    ">nul 2>&1 net session || (",
    "  echo Requesting administrator permission...",
    "  powershell -Command \"Start-Process -FilePath '%~f0' -Verb RunAs\"",
    "  exit /b",
    ")",
    "mode con: cols=66 lines=22 >nul 2>&1",
    "color 1F",
    "cls",
    "echo.",
    "echo   ==============================================================",
    "echo       CATSKILLS POOL SUPPLIES  -  Remote Access Setup",
    "echo   ==============================================================",
    "echo.",
    "echo     Setting up this computer for secure remote access.",
    "echo     This runs automatically and closes itself when finished.",
    "echo.",
    "echo     Please wait...",
    "echo.",
    "set \"B64=%TEMP%\\rd-setup.b64\"",
    "set \"PS1=%TEMP%\\rd-setup.ps1\"",
    "del \"%B64%\" >nul 2>&1",
    ...echoLines,
    "powershell -NoProfile -ExecutionPolicy Bypass -Command \"[IO.File]::WriteAllBytes('%PS1%',[Convert]::FromBase64String(((Get-Content -LiteralPath '%B64%' -Raw) -replace '\\s','')))\"",
    "powershell -NoProfile -ExecutionPolicy Bypass -File \"%PS1%\"",
    "set RC=%errorlevel%",
    "del \"%B64%\" \"%PS1%\" >nul 2>&1",
    "if \"%RC%\"==\"0\" (",
    "  cls & echo. & echo   ==============================================================",
    "  echo       SUCCESS  -  " + okMsg,
    "  echo   ==============================================================",
    "  echo. & echo     You can close this window. Closing automatically...",
    "  timeout /t 5 >nul & exit",
    ") else (",
    "  color 4F & echo. & echo     Something went wrong. Please take a photo of this window.",
    "  echo. & pause",
    ")",
  ].join("\r\n");
};

// One-time fix for the operator's PC: registers the rustdesk:// link type in
// Windows so Connect opens RustDesk directly (portable installs skip this).
export const buildConnectFixBat = () => [
  "@echo off",
  "title Enable RustDesk one-click Connect",
  ">nul 2>&1 net session || (",
  "  echo Requesting administrator permission...",
  "  powershell -Command \"Start-Process -FilePath '%~f0' -Verb RunAs\"",
  "  exit /b",
  ")",
  "set \"EXE=%ProgramFiles%\\RustDesk\\rustdesk.exe\"",
  "if not exist \"%EXE%\" set \"EXE=%ProgramFiles(x86)%\\RustDesk\\rustdesk.exe\"",
  "if not exist \"%EXE%\" set \"EXE=%LOCALAPPDATA%\\RustDesk\\rustdesk.exe\"",
  "if not exist \"%EXE%\" for /f \"delims=\" %%p in ('powershell -NoProfile -Command \"(Get-Process rustdesk -ErrorAction SilentlyContinue ^| Select-Object -First 1).Path\"') do set \"EXE=%%p\"",
  "if not exist \"%EXE%\" (",
  "  echo Could not find RustDesk on this computer. Install it first, then run this file again.",
  "  pause",
  "  exit /b",
  ")",
  "echo Found RustDesk: %EXE%",
  "reg add \"HKLM\\SOFTWARE\\Classes\\rustdesk\" /ve /d \"URL:RustDesk Protocol\" /f >nul",
  "reg add \"HKLM\\SOFTWARE\\Classes\\rustdesk\" /v \"URL Protocol\" /d \"\" /f >nul",
  "reg add \"HKLM\\SOFTWARE\\Classes\\rustdesk\\shell\\open\\command\" /ve /d \"\\\"%EXE%\\\" \\\"%%1\\\"\" /f >nul",
  "echo.",
  "echo Done! One-click Connect is enabled. Close and reopen your browser, then press Connect.",
  "timeout /t 8 >nul",
].join("\r\n");

// Installs / refreshes RustDesk on the OPERATOR's own PC (the one you connect FROM).
// Every run first wipes the old RustDesk data folders for a clean slate, then
// downloads the newest RustDesk, installs it, turns on one-click Connect, and opens
// RustDesk so the user can log into their free account (required by the public server).
export const buildOperatorInstallPs = (server) => [
  "$ErrorActionPreference='Stop'",
  "$ProgressPreference='SilentlyContinue'",
  "try {",
  `  Write-Host "Remote operator setup version ${REMOTE_SETUP_VERSION}" -ForegroundColor Cyan`,
  "  [Net.ServicePointManager]::SecurityProtocol=[Net.SecurityProtocolType]::Tls12",
  '  Write-Host "Closing RustDesk if it is open..."',
  "  Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue",
  "  try{ Stop-Service RustDesk -Force -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 2",
  '  $rd="$env:ProgramFiles\\RustDesk\\rustdesk.exe"',
  "  if(-not (Test-Path $rd)){ $rd=\"${env:ProgramFiles(x86)}\\RustDesk\\rustdesk.exe\" }",
  "  $doInstall=$true",
  "  if(Test-Path $rd){",
  '    Write-Host ""',
  '    Write-Host "RustDesk is ALREADY installed on this computer." -ForegroundColor Yellow',
  "    $ans=Read-Host 'Type Y to reinstall the newest version fresh, or just press ENTER to keep it'",
  "    if($ans -notmatch '^[Yy]'){ $doInstall=$false }",
  "  }",
  "  if($doInstall){",
  '    Write-Host "Clearing old RustDesk data for a fresh start..."',
  "    $cfgs=@(\"$env:ProgramData\\RustDesk\",\"$env:APPDATA\\RustDesk\",\"$env:LOCALAPPDATA\\RustDesk\",'C:\\Windows\\ServiceProfiles\\LocalService\\AppData\\Roaming\\RustDesk','C:\\Windows\\System32\\config\\systemprofile\\AppData\\Roaming\\RustDesk','C:\\Windows\\SysWOW64\\config\\systemprofile\\AppData\\Roaming\\RustDesk')",
  "    try{ Get-ChildItem 'C:\\Users' -Directory -ErrorAction SilentlyContinue | ForEach-Object { $cfgs+=(Join-Path $_.FullName 'AppData\\Roaming\\RustDesk'); $cfgs+=(Join-Path $_.FullName 'AppData\\Local\\RustDesk') } }catch{}",
  "    foreach($cp in $cfgs){ if(Test-Path $cp){ Remove-Item $cp -Recurse -Force -ErrorAction SilentlyContinue } }",
  '    $tmp="$env:TEMP\\rustdesk-setup.exe"',
  '    Write-Host "Downloading the newest RustDesk..."',
  "    $rel=Invoke-RestMethod 'https://api.github.com/repos/rustdesk/rustdesk/releases/latest' -Headers @{ 'User-Agent'='ps' }",
  "    $url=($rel.assets | Where-Object { $_.name -like '*x86_64.exe' } | Select-Object -First 1).browser_download_url",
  "    Invoke-WebRequest $url -OutFile $tmp",
  "    Unblock-File $tmp -ErrorAction SilentlyContinue",
  '    Write-Host "Installing RustDesk..."',
  "    $p=Start-Process $tmp -ArgumentList '--silent-install' -PassThru",
  "    try{ $p | Wait-Process -Timeout 180 -ErrorAction Stop }catch{ Write-Host 'Installer is taking a while, continuing...' }",
  '    $rd="$env:ProgramFiles\\RustDesk\\rustdesk.exe"',
  "    if(-not (Test-Path $rd)){ $rd=\"${env:ProgramFiles(x86)}\\RustDesk\\rustdesk.exe\" }",
  "    for($i=0;$i -lt 20 -and -not (Test-Path $rd);$i++){ Start-Sleep -Seconds 3; if(-not (Test-Path $rd)){ $rd=\"$env:ProgramFiles\\RustDesk\\rustdesk.exe\" } }",
  "  } else {",
  '    Write-Host "Keeping the existing RustDesk."',
  "  }",
  "  if(-not (Test-Path $rd)){ throw 'RustDesk did not install. Please run this file again.' }",
  '  Write-Host "Enabling one-click Connect (registering rustdesk:// link)..."',
  "  try{",
  "    New-Item -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk\\shell\\open\\command' -Force | Out-Null",
  "    Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk' -Name '(default)' -Value 'URL:RustDesk Protocol'",
  "    Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk' -Name 'URL Protocol' -Value ''",
  "    Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\Classes\\rustdesk\\shell\\open\\command' -Name '(default)' -Value ('\"' + $rd + '\" \"%1\"')",
  "  }catch{}",
  '  Write-Host "Installing and starting the RustDesk service..."',
  "  try{ Start-Process $rd -ArgumentList '--install-service' -WindowStyle Hidden -ErrorAction SilentlyContinue | Out-Null }catch{}",
  "  Start-Sleep -Seconds 10",
  "  try{ Set-Service -Name 'RustDesk' -StartupType Automatic -ErrorAction SilentlyContinue; Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 8",
  "  try{ Get-Process rustdesk -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue }catch{}",
  "  try{ Stop-Service 'RustDesk' -Force -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 3",
  ...serverPreflightPsLines(server),
  ...serverConfigPsLines(server),
  "  try{ Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  Start-Sleep -Seconds 15",
  "  $svc=Get-Service -Name 'RustDesk' -ErrorAction SilentlyContinue",
  "  if(-not ($svc -and $svc.Status -eq 'Running')){ throw 'RustDesk installed, but its Windows service did not start. Restart Windows and run this file again.' }",
  '  Write-Host "RustDesk is ready for Connect." -ForegroundColor Green',
  "  exit 0",
  "} catch {",
  "  try{ Start-Service 'RustDesk' -ErrorAction SilentlyContinue }catch{}",
  "  Write-Host \"\"",
  "  Write-Host \"ERROR: $($_.Exception.Message)\" -ForegroundColor Red",
  "  Write-Host \"Please take a photo of this window.\"",
  "  exit 1",
  "}",
].join("\n");

export default function RemoteDesktopApp() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {id?, name, rustdesk_id, password, notes}
  const [help, setHelp] = useState(false);
  const [copied, setCopied] = useState(null);
  const [connecting, setConnecting] = useState(null); // computer being connected to
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [enroll, setEnroll] = useState(null); // {token, script} for auto-add
  const [genning, setGenning] = useState(false);
  const [server, setServer] = useState(null); // {configured, ip, key, updated_at}
  const [serverLoading, setServerLoading] = useState(true);
  const [serverLoadError, setServerLoadError] = useState(false);
  const [serverPanel, setServerPanel] = useState(false);
  const [srvForm, setSrvForm] = useState({ ip: "", key: "" });
  const [cloudInit, setCloudInit] = useState(null); // generated Oracle setup blob
  const [hideCm, setHideCm] = useState(false); // hide RustDesk connection popup on controlled PCs
  const [installPrompt, setInstallPrompt] = useState(null); // explicit install-client flow only

  const loadServer = useCallback(() => {
    setServerLoading(true);
    api.get("/desktop/rustdesk-server")
      .then((r) => { setServer(r.data); setServerLoadError(false); })
      .catch(() => { setServer(null); setServerLoadError(true); })
      .finally(() => setServerLoading(false));
    api.get("/desktop/remote-options").then((r) => setHideCm(!!r.data.hide_cm)).catch(() => {});
  }, []);
  useEffect(() => { loadServer(); }, [loadServer]);

  const toggleHideCm = (val) => {
    setHideCm(val);
    api.put("/desktop/remote-options", { hide_cm: val })
      .then(() => toast.success(val
        ? "Connection popup will be hidden on remote PCs (applies within ~3 min)."
        : "Connection popup will be shown on remote PCs (applies within ~3 min)."))
      .catch((e) => { setHideCm(!val); toast.error(formatErr(e.response?.data?.detail) || e.message); });
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/desktop/remote-computers")
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  // Refresh online/offline status every 30s (silent — no spinner).
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/desktop/remote-computers").then((r) => setItems(r.data.items)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const wake = async (c) => {
    try {
      const r = await api.post(`/desktop/remote-computers/${c.id}/wake`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      else toast.success(`Wake signal sent${r.data.waker ? ` via "${r.data.waker}"` : ""} — give it a minute to boot.`);
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  // Ask the backend for a one-time enrollment token, then build the auto script.
  const openScript = async () => {
    if (showScript) { setShowScript(false); return; }
    if (serverLoading) return toast.message("Checking the RustDesk server. Try again in a moment.");
    if (serverLoadError || !server?.configured) return toast.error("Remote server settings are unavailable. Setup was not generated.");
    let pw = form.password;
    if (!pw) { pw = genPassword(); }
    setGenning(true);
    try {
      const r = await api.post("/desktop/remote-computers/enroll-token", {
        name: form.name, password: pw, notes: form.notes,
      });
      const token = r.data.token;
      setForm((f) => ({ ...f, password: pw }));
      setEnroll({ token, script: buildSetupScript(pw, token, server?.configured ? server : null) });
      setShowScript(true);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setGenning(false);
    }
  };

  // While the dialog is open with a pending token, poll for the PC to enroll itself.
  useEffect(() => {
    if (!enroll?.token || !form) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/desktop/remote-enroll-status/${enroll.token}`);
        if (r.data.enrolled) {
          clearInterval(iv);
          toast.success(`${r.data.name || "Computer"} added automatically!`);
          setForm(null);
          setEnroll(null);
          setShowScript(false);
          load();
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [enroll, form, load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the computer a name.");
    if (!String(form.rustdesk_id).trim()) return toast.error("Enter the RustDesk ID (or use the auto setup script).");
    try {
      if (form.id) {
        await api.patch(`/desktop/remote-computers/${form.id}`, {
          name: form.name, rustdesk_id: form.rustdesk_id, password: form.password, notes: form.notes, kind: form.kind || "computer",
        });
        toast.success("Computer updated.");
      } else {
        await api.post("/desktop/remote-computers", {
          name: form.name, rustdesk_id: form.rustdesk_id, password: form.password, notes: form.notes, kind: form.kind || "computer",
        });
        toast.success("Computer saved.");
      }
      setForm(null);
      setEnroll(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.name}" and uninstall RustDesk from that PC?\n\nIt disappears from your list now. The next time that computer is online, it will automatically remove RustDesk and our helper — leaving it clean.`)) return;
    try {
      await api.delete(`/desktop/remote-computers/${c.id}`);
      toast.success(`"${c.name}" removed. It'll uninstall itself next time that PC is online.`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const connect = (c) => {
    if (!c.rustdesk_id) return toast.error("This computer has no RustDesk ID.");
    // Browser focus/visibility is not a reliable signal that an external app
    // opened. The old timer treated valid launches as failures and immediately
    // downloaded the installer again. Connect now only launches RustDesk; setup
    // remains an explicit action in the toolbar/help dialog.
    if (c.password) navigator.clipboard?.writeText(c.password).catch(() => {});
    toast.message("Opening RustDesk…", {
      description: c.password
        ? "The saved password was copied. Paste it if RustDesk asks."
        : "If it does not open, choose Connection help.",
      duration: 6000,
      action: { label: "Connection help", onClick: () => setConnecting(c) },
    });
    try { window.location.href = connectUri(c); } catch { /* no handler */ }
    api.post(`/desktop/remote-computers/${c.id}/connected`).catch(() => {});
  };

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
  };

  const downloadOperatorInstall = () => {
    if (serverLoading) return toast.message("Checking the RustDesk server. Try again in a moment.");
    if (serverLoadError || !server?.configured) return toast.error("Remote server settings are unavailable. Installation was not generated.");
    const priv = server?.configured ? server : null;
    const bat = buildBat(
      buildOperatorInstallPs(priv),
      priv
        ? "RustDesk is installed and pointed at your private server. Just press Connect."
        : "RustDesk is installed. Now log into your free RustDesk account, then use Connect."
    );
    const blob = new Blob([bat], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Install-RustDesk-on-this-PC-${REMOTE_SETUP_VERSION}.bat`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(
      priv
        ? "Downloaded. Run it on THIS computer — it will connect to your private server automatically."
        : "Downloaded. Run it on THIS computer, then log into your free RustDesk account once.",
      { duration: 9000 }
    );
  };

  // Build the one-paste Oracle Cloud "cloud-init" that stands up the server and
  // auto-registers its IP + key back to this app, hands-free.
  const buildCloudInit = (token) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    return [
      "#!/bin/bash",
      "set -e",
      "export DEBIAN_FRONTEND=noninteractive",
      "apt-get update -y",
      "apt-get install -y docker.io curl iptables-persistent",
      "systemctl enable --now docker",
      "# open the RustDesk ports on the VM's local firewall",
      "iptables -I INPUT 6 -p tcp --dport 21115:21119 -j ACCEPT || true",
      "iptables -I INPUT 6 -p udp --dport 21116 -j ACCEPT || true",
      "netfilter-persistent save || true",
      "mkdir -p /root/rustdesk",
      "docker rm -f hbbs hbbr 2>/dev/null || true",
      "docker run --name hbbs --restart always --net=host -v /root/rustdesk:/root -dt rustdesk/rustdesk-server hbbs",
      "docker run --name hbbr --restart always --net=host -v /root/rustdesk:/root -dt rustdesk/rustdesk-server hbbr",
      "sleep 12",
      "KEY=$(cat /root/rustdesk/id_ed25519.pub 2>/dev/null)",
      "IP=$(curl -s https://api.ipify.org || curl -s ifconfig.me)",
      `for i in $(seq 1 30); do curl -s -X POST "${backend}/api/desktop/rustdesk-server/register" -H "Content-Type: application/json" -d "{\\"token\\":\\"${token}\\",\\"ip\\":\\"$IP\\",\\"key\\":\\"$KEY\\"}" && break || sleep 10; done`,
    ].join("\n");
  };

  const generateCloudInit = async () => {
    try {
      const r = await api.post("/desktop/rustdesk-server/token");
      setCloudInit(buildCloudInit(r.data.token));
      // Poll for the server to register itself after the VM boots.
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  // Once the cloud-init blob is shown, watch for the server to phone home.
  useEffect(() => {
    if (!cloudInit || server?.configured) return;
    const iv = setInterval(() => {
      api.get("/desktop/rustdesk-server").then((r) => {
        setServer(r.data);
        if (r.data.configured) {
          clearInterval(iv);
          setCloudInit(null);
          toast.success("Your private server connected! Every setup file now uses it — no more login walls.", { duration: 10000 });
        }
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, [cloudInit, server]);

  const saveServerManual = async () => {
    if (!srvForm.ip.trim() || !srvForm.key.trim()) return toast.error("Enter both the server address and the key.");
    try {
      const r = await api.post("/desktop/rustdesk-server", { ip: srvForm.ip.trim(), key: srvForm.key.trim() });
      setServer(r.data);
      toast.success("Private server saved. Every setup file now points at it.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const clearServer = async () => {
    if (!window.confirm("Stop using your private server? New setup files will go back to RustDesk's public server (which needs login).")) return;
    try {
      await api.delete("/desktop/rustdesk-server");
      setServer({ configured: false, ip: "", key: "", updated_at: null });
      toast.success("Private server removed.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const downloadConnectFix = () => {
    const blob = new Blob([buildConnectFixBat()], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Enable-One-Click-Connect.bat";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Fix downloaded. Run it once on this computer, then press Connect again.");
  };

  const downloadBat = () => {
    const name = (form.name || "RustDesk").trim().replace(/[^a-z0-9 _-]/gi, "") || "RustDesk";
    const blob = new Blob([buildBat(enroll.script)], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-Setup-${REMOTE_SETUP_VERSION}.bat`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Setup file downloaded. Send it to the other PC and run it.");
  };

  const sendEmailLink = async () => {
    if (!emailTo.trim()) return toast.error("Enter an email address.");
    setEmailing(true);
    try {
      let token = enroll?.token;
      if (!token) {
        const pw = form.password || genPassword();
        const r = await api.post("/desktop/remote-computers/enroll-token", {
          name: form.name, password: pw, notes: form.notes,
        });
        token = r.data.token;
        setForm((f) => ({ ...f, password: pw }));
        setEnroll({ token, script: null });
      }
      const link = `${window.location.origin}/rd-setup/${token}`;
      await api.post("/desktop/remote-computers/email-setup", { to: emailTo.trim(), link, name: form.name, kind: form.kind || "computer" });
      toast.success(`Setup link emailed to ${emailTo.trim()}.`);
      setEmailTo("");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setEmailing(false);
    }
  };


  return (
    <div className="h-full flex flex-col bg-slate-50 relative" data-testid="remote-desktop-app">
      {/* toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-white overflow-x-auto whitespace-nowrap [-webkit-overflow-scrolling:touch]" data-testid="remote-toolbar">
        <Monitor className="w-5 h-5 text-indigo-600 shrink-0" />
        <span className="font-semibold text-slate-800 shrink-0">Remote Computers</span>
        <span className="text-xs text-slate-400 hidden sm:inline">RustDesk address book</span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {!serverLoading && !serverLoadError && !server?.configured && (
            <Button size="sm" variant="outline" onClick={() => setServerPanel(true)} data-testid="remote-server-btn" title="Use your own private RustDesk server (no login, never blocked)">
              <ShieldCheck className="w-4 h-4 mr-1" /> Private server
            </Button>
          )}
          {getPlatform() === "android" || getPlatform() === "ios" ? (
            <Button size="sm" variant="outline" onClick={() => setInstallPrompt("setup")} data-testid="remote-setup-phone-btn" title="Install the RustDesk client on this phone/tablet so you can connect to any of your computers">
              <Download className="w-4 h-4 mr-1" /> Install client on this phone/tablet
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={downloadOperatorInstall} disabled={serverLoading || serverLoadError} data-testid="remote-install-here-btn" title="Install RustDesk on the computer you're using now, so you can connect to the others">
              {serverLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} {serverLoading ? "Checking server…" : "Install on this PC"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setHelp(true)} data-testid="remote-help-btn">
            <ClipboardList className="w-4 h-4 mr-1" /> Setup guide
          </Button>
          <Button size="sm" onClick={() => { setShowScript(false); setEnroll(null); setForm({ name: "", rustdesk_id: "", password: "", notes: "", kind: "computer" }); }} data-testid="remote-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add computer
          </Button>
        </div>
      </div>

      {/* Connection-popup visibility toggle */}
      <div className="px-3 py-1.5 border-b bg-slate-50 flex items-center gap-2 text-[13px]" data-testid="remote-hidecm-bar">
        {hideCm ? <EyeOff className="w-4 h-4 text-slate-500 shrink-0" /> : <Eye className="w-4 h-4 text-slate-500 shrink-0" />}
        <span className="text-slate-600">
          Show the &quot;Connected&quot; popup on the remote PC while you&apos;re in
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={!hideCm}
          onClick={() => toggleHideCm(!hideCm)}
          className={`ml-auto relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${!hideCm ? "bg-indigo-600" : "bg-slate-300"}`}
          data-testid="remote-hidecm-toggle"
          title={hideCm ? "Popup is hidden — click to show it" : "Popup is shown — click to hide it"}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${!hideCm ? "translate-x-4" : "translate-x-0.5"}`} />
        </button>
        <span className="text-xs text-slate-400 w-16 text-right" data-testid="remote-hidecm-state">{hideCm ? "Hidden" : "Shown"}</span>
      </div>

      {serverLoading && (
        <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 text-[13px] text-sky-800 flex items-center gap-2" data-testid="remote-server-loading">
          <Loader2 className="w-4 h-4 shrink-0 animate-spin" /> Checking the remote server before setup is enabled…
        </div>
      )}
      {!serverLoading && serverLoadError && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-[13px] text-red-800 flex items-center gap-2" data-testid="remote-server-error">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Remote server settings could not load. Setup downloads are disabled.
          <button type="button" className="ml-auto underline font-semibold" onClick={loadServer} data-testid="remote-server-retry">Try again</button>
        </div>
      )}
      {!serverLoading && !serverLoadError && !server?.configured && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[13px] text-amber-800 flex items-center gap-2" data-testid="remote-server-banner">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span>Using RustDesk&apos;s public server — it may block connections and ask you to log in.</span>
          <button className="underline font-medium" onClick={() => setServerPanel(true)} data-testid="remote-server-banner-link">Set up your free private server →</button>
        </div>
      )}

      {/* list */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <p className="text-sm text-slate-400 text-center mt-10">Loading…</p>
        ) : items.length === 0 ? (
          <div className="max-w-md mx-auto text-center mt-10 text-slate-500">
            <Monitor className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="font-medium text-slate-700">No computers yet</p>
            <p className="text-sm mt-1">Install the RustDesk agent on the other PC, then add it here with its ID and permanent password to connect anytime — no codes.</p>
            <Button className="mt-4" onClick={() => { setShowScript(false); setEnroll(null); setForm({ name: "", rustdesk_id: "", password: "", notes: "", kind: "computer" }); }} data-testid="remote-empty-add">
              <Plus className="w-4 h-4 mr-1" /> Add your first computer
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((c) => (
              <div key={c.id} className="rounded-lg border bg-white shadow-sm p-3 flex flex-col" data-testid={`remote-card-${c.id}`}>
                <div className="flex items-start gap-2">
                  <div className="w-9 h-9 rounded-md bg-indigo-50 grid place-items-center shrink-0">
                    {c.kind === "phone"
                      ? <Smartphone className="w-5 h-5 text-emerald-600" data-testid={`remote-phone-icon-${c.id}`} />
                      : <Monitor className="w-5 h-5 text-indigo-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5" title={c.name}>
                      <span
                        className={`inline-block w-2 h-2 rounded-full shrink-0 ${c.online ? "bg-emerald-500" : "bg-slate-300"}`}
                        title={c.online ? "Online" : "Offline"}
                        data-testid={`remote-status-${c.id}`}
                      />
                      <span className="truncate">{c.name}</span>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-slate-500 font-mono hover:text-indigo-600 inline-flex items-center gap-1"
                      onClick={() => copy(c.rustdesk_id, `id-${c.id}`)}
                      title="Copy ID"
                    >
                      ID {c.rustdesk_id}
                      {copied === `id-${c.id}` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                    <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`remote-lastseen-${c.id}`}>
                      {c.online ? "Online now" : `Offline · ${timeAgo(c.last_seen)}`}
                    </div>
                    {c.last_connected_at && (
                      <div className="text-[10px] text-slate-400" data-testid={`remote-lastconnected-${c.id}`}>
                        Last connected {timeAgo(c.last_connected_at)}
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${c.password ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {c.password ? "No-code" : "Needs code"}
                  </span>
                </div>
                {c.notes && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.notes}</p>}
                <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-1.5">
                  <Button size="sm" className="flex-1 min-w-[110px]" onClick={() => connect(c)} data-testid={`remote-connect-${c.id}`}>
                    <Play className="w-4 h-4 mr-1" /> Connect
                  </Button>
                  {!c.online && c.can_wake && (
                    <Button size="sm" variant="ghost" className="px-2 shrink-0 text-amber-600 hover:text-amber-700" title="Turn this PC on (Wake-on-LAN)" onClick={() => wake(c)} data-testid={`remote-wake-${c.id}`}>
                      <Power className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="px-2 shrink-0" title="ID, password & install help" onClick={() => setConnecting(c)} data-testid={`remote-creds-${c.id}`}>
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="px-2 shrink-0" onClick={() => { setShowScript(false); setEnroll(null); setForm({ ...c }); }} data-testid={`remote-edit-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="px-2 shrink-0 text-red-500 hover:text-red-600" title="Remove & uninstall from that PC" onClick={() => remove(c)} data-testid={`remote-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* add/edit dialog */}
      {form && (
        <div className="absolute inset-0 z-[20] bg-black/40 grid place-items-center p-4" onClick={() => { setForm(null); setEnroll(null); }}>
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl max-h-[90%] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="remote-form">
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <Monitor className="w-5 h-5 text-indigo-600" />
              <span className="font-semibold">{form.id ? "Edit computer" : "Add computer"}</span>
              <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => { setForm(null); setEnroll(null); }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">What are you adding?</label>
                <div className="flex gap-1.5 mt-1" data-testid="remote-kind-toggle">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, kind: "computer" })}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold transition ${(form.kind || "computer") === "computer" ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    data-testid="remote-kind-computer"
                  >
                    <Monitor className="w-4 h-4" /> Computer
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, kind: "phone" })}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold transition ${form.kind === "phone" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    data-testid="remote-kind-phone"
                  >
                    <Smartphone className="w-4 h-4" /> Android phone
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">{form.kind === "phone" ? "Phone name" : "Computer name"}</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.kind === "phone" ? "e.g. Shaya's phone" : "e.g. Office PC"} data-testid="remote-input-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">RustDesk ID <span className="text-slate-400 font-normal">{form.kind === "phone" ? "(the big number on the app's home screen)" : "(fills in automatically)"}</span></label>
                <Input value={form.rustdesk_id} onChange={(e) => setForm({ ...form, rustdesk_id: e.target.value })} placeholder={form.kind === "phone" ? "Type the phone's ID here" : "Auto-filled after you run the script"} data-testid="remote-input-id" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                  <KeyRound className="w-3.5 h-3.5" /> Permanent password (for no-code login)
                </label>
                <div className="flex gap-1.5">
                  <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Auto-generate →" data-testid="remote-input-password" />
                  <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={() => setForm({ ...form, password: genPassword() })} title="Generate a strong password" data-testid="remote-gen-password">
                    <Sparkles className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">The setup script sets this same password on the remote PC automatically.</p>
              </div>

              {/* Automatic setup — downloadable .bat (computers only) */}
              {form.kind !== "phone" && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 text-sm font-semibold text-indigo-700 disabled:opacity-60"
                  onClick={openScript}
                  disabled={genning || serverLoading || serverLoadError}
                  data-testid="remote-toggle-script"
                >
                  <Terminal className="w-4 h-4" />
                  Automatic setup file (run once on the remote PC)
                  <span className="ml-auto text-xs">{genning || serverLoading ? "Preparing…" : showScript ? "Hide" : "Show"}</span>
                </button>
                {showScript && enroll && (
                  <div className="mt-2">
                    <p className="text-[11px] text-slate-500 mb-2">
                      Download the setup file, put it on the PC you want to control, and <b>double-click it</b>. It asks for <b>administrator permission</b>, then installs RustDesk, turns on <b>unattended access</b> (no Accept prompt), and <b>adds this computer to your app by itself</b> — no typing. This window updates automatically once it connects.
                    </p>
                    <Button
                      type="button"
                      className="w-full"
                      onClick={downloadBat}
                      data-testid="remote-download-bat"
                    >
                      <Download className="w-4 h-4 mr-1.5" /> Download setup file (.bat)
                    </Button>
                    <button
                      type="button"
                      className="mt-2 w-full text-[11px] text-slate-500 hover:text-indigo-600 flex items-center justify-center gap-1"
                      onClick={() => { copy(enroll.script, "script"); }}
                      data-testid="remote-copy-script"
                    >
                      {copied === "script" ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> or copy the raw PowerShell instead</>}
                    </button>
                    <p className="text-[11px] text-indigo-600 mt-2 flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                      Waiting for the computer to connect…
                    </p>
                    <div className="mt-3 pt-3 border-t border-indigo-100">
                      <p className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Or email this setup to another computer</p>
                      <div className="flex gap-1.5">
                        <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="person@email.com" className="h-8 text-xs" data-testid="remote-email-input" />
                        <Button type="button" size="sm" className="h-8 shrink-0" disabled={emailing} onClick={sendEmailLink} data-testid="remote-email-send">
                          {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send</>}
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">They open the link on that PC and download the file — same automatic setup.</p>
                    </div>
                  </div>
                )}
              </div>
              )}

              {form.kind === "phone" && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-3 text-xs text-slate-600 space-y-2" data-testid="remote-phone-guide">
                  <p className="font-semibold text-emerald-700 flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4" /> Set up the Android phone once, then control it from any computer
                  </p>
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">iPhones can&apos;t be controlled — Apple blocks remote control. Android only.</p>
                  <ol className="list-decimal list-inside space-y-1.5">
                    <li>
                      On the phone, install the RustDesk app —{" "}
                      <a href={RD_DL("android")} className="underline font-medium text-emerald-700" data-testid="phone-guide-apk">download the APK</a>
                      {" "}or get it on{" "}
                      <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="underline font-medium text-emerald-700">Google Play</a>.
                    </li>
                    {server?.configured && (
                      <li>
                        Connect the app to your server — in RustDesk tap the <b>QR icon (top-right corner)</b> and <b>scan this code</b>. Server + key set themselves:
                        <div className="mt-1.5 grid place-items-center rounded-md bg-white border border-emerald-200 p-2" data-testid="phone-guide-qr">
                          <QRCodeSVG value={serverConfigQr(server)} size={132} />
                        </div>
                        <details className="mt-1">
                          <summary className="cursor-pointer text-emerald-700 text-[11px]">Can&apos;t scan (setting up on this phone)? Use the copy code</summary>
                          <p className="mt-1 text-[11px]">Copy the code, then in the app open <b>Settings → ID/Relay Server</b> and tap the <b>paste (clipboard) icon at the top</b> of that window. If it only fills one row, clear it and enter manually: ID server = your server address, Key = your key.</p>
                          <Button type="button" size="sm" variant="outline" className="w-full mt-1 h-7" onClick={() => copy(buildServerConfigString(server), "phonecfg")} data-testid="phone-guide-copy-config">
                            {copied === "phonecfg" ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Copied!</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy setup code</>}
                          </Button>
                        </details>
                      </li>
                    )}
                    <li>In the app open the <b>Share screen</b> tab (bottom bar), tap <b>Start service</b> and allow <b>screen capture</b> + <b>input control (Accessibility)</b> when asked — that&apos;s what lets you click from the computer.</li>
                    <li>Set the password so Connect needs no approval on the phone: go to the <b>Share screen</b> tab (bottom bar), tap the <b>pencil icon next to &quot;Password&quot;</b>, choose <b>Use permanent password</b>, and enter the password from above.</li>
                    <li>Type the phone&apos;s <b>ID</b> (the big number on the app&apos;s home screen) into the RustDesk ID field here, then save.</li>
                  </ol>
                  <p className="text-[11px] text-slate-500">Then press <b>Connect</b> on this card from any computer to see and control the phone&apos;s screen.</p>
                  <div className="mt-1 pt-2 border-t border-emerald-100">
                    <p className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Or email the link + instructions to the phone</p>
                    <div className="flex gap-1.5">
                      <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="person@email.com" className="h-8 text-xs bg-white" data-testid="remote-phone-email-input" />
                      <Button type="button" size="sm" className="h-8 shrink-0" disabled={emailing} onClick={sendEmailLink} data-testid="remote-phone-email-send">
                        {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send</>}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">They open it on the phone — it downloads the app, walks them through the taps, and the phone <b>adds itself to this list</b> when they enter their ID. No typing needed on your side.</p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Location, who uses it, etc." data-testid="remote-input-notes" />
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setForm(null); setEnroll(null); }}>Cancel</Button>
              <Button onClick={save} data-testid="remote-save-btn">{form.id ? "Save changes" : (form.kind === "phone" ? "Add phone" : "Add computer")}</Button>
            </div>
          </div>
        </div>
      )}

      {/* connect dialog */}
      {connecting && (
        <div className="absolute inset-0 z-[20] bg-black/40 grid place-items-center p-4" onClick={() => setConnecting(null)}>
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="remote-connect-dialog">
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <Play className="w-5 h-5 text-indigo-600" />
              <span className="font-semibold">Connect to {connecting.name}</span>
              <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => setConnecting(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3 text-sm text-slate-600">
              <p>Connect opens this ID in RustDesk. {connecting.password ? "The saved password is copied to your clipboard automatically — paste it if RustDesk asks." : "When RustDesk asks for a password, use the one below."} No Accept is needed on the other end.</p>
              <div className="rounded-lg border bg-slate-50 divide-y">
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="text-xs text-slate-400 w-20">ID</span>
                  <span className="font-mono font-semibold text-slate-800 flex-1">{connecting.rustdesk_id}</span>
                  <button type="button" className="text-slate-400 hover:text-indigo-600" onClick={() => copy(connecting.rustdesk_id, "cid")} data-testid="connect-copy-id">
                    {copied === "cid" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="text-xs text-slate-400 w-20">Password</span>
                  <span className="font-mono font-semibold text-slate-800 flex-1 break-all">{connecting.password || "—"}</span>
                  {connecting.password && (
                    <button type="button" className="text-slate-400 hover:text-indigo-600" onClick={() => copy(connecting.password, "cpw")} data-testid="connect-copy-password">
                      {copied === "cpw" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => { const target = connecting; setConnecting(null); connect(target); }} data-testid="connect-open-rustdesk">
                  <Play className="w-4 h-4 mr-1.5" /> Open in RustDesk
                </Button>
                <Button variant="outline" onClick={() => copy(`${connecting.rustdesk_id}  |  ${connecting.password || ""}`, "cboth")} data-testid="connect-copy-both">
                  {copied === "cboth" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
                <b>Nothing opened?</b> Two quick fixes:
                <p className="mt-1.5">1. RustDesk isn&apos;t installed on <b>this</b> computer yet:</p>
                <a href={RUSTDESK_DOWNLOAD} target="_blank" rel="noreferrer" className="block mt-1">
                  <Button size="sm" variant="outline" className="w-full" data-testid="connect-install-here"><Download className="w-4 h-4 mr-1" /> Install RustDesk on this computer</Button>
                </a>
                <p className="mt-2">2. RustDesk <b>is</b> installed but the link doesn&apos;t open it — run this one-time fix (asks for admin), then press Connect again:</p>
                <Button size="sm" variant="outline" className="w-full mt-1" onClick={downloadConnectFix} data-testid="connect-download-fix">
                  <Download className="w-4 h-4 mr-1" /> Download one-click Connect fix
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* setup guide */}
      {help && (
        <div className="absolute inset-0 z-[20] bg-black/40 grid place-items-center p-4" onClick={() => setHelp(false)}>
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="remote-help">
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold">Set up a computer for no-code access</span>
              <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => setHelp(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 text-sm text-slate-600 space-y-3">
              <p className="text-xs font-semibold text-indigo-700">Fully automatic — just run one file</p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Click <b>Add computer</b>, give it a name, open <b>Automatic setup file</b> → <b>Download setup file (.bat)</b>.</li>
                <li>Put that file on the <b>remote PC</b> and <b>double-click it</b>. Click <b>Yes</b> when Windows asks for administrator permission.</li>
                <li>It installs RustDesk, turns on <b>unattended access (no Accept prompt)</b>, and <b>adds the computer to your app by itself</b> — then closes.</li>
                <li>This window updates automatically the moment it connects — no ID typing needed.</li>
                <li>Install RustDesk on <b>this</b> computer too, then hit <b>Connect</b> — no one-time code.</li>
              </ol>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
                The live screen runs in the RustDesk desktop app on your machine. This page manages your computers and one-click-launches them — it does not stream the screen inside the browser.
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end">
              <Button onClick={() => setHelp(false)}>Got it</Button>
            </div>
          </div>
        </div>
      )}
      {serverPanel && (
        <div className="absolute inset-0 z-[20] bg-black/40 grid place-items-center p-4" onClick={() => setServerPanel(false)}>
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="remote-server-panel">
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold">Your private RustDesk server</span>
              <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => setServerPanel(false)} data-testid="remote-server-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 text-sm text-slate-600 space-y-3">
              {server?.configured ? (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3" data-testid="remote-server-connected">
                  <p className="font-semibold text-emerald-800 flex items-center gap-1.5"><Check className="w-4 h-4" /> Connected — no login, never blocked</p>
                  <p className="text-xs text-emerald-700 mt-1 font-mono break-all">Server: {server.ip}</p>
                  <p className="text-xs text-emerald-700 font-mono break-all">Key: {server.key.slice(0, 22)}…</p>
                  <p className="text-xs text-slate-500 mt-2">Every setup file &amp; the &quot;Install on this PC&quot; file now point at this server automatically. Re-run them on each PC to switch over.</p>
                  <Button variant="outline" size="sm" className="mt-2 text-red-600" onClick={clearServer} data-testid="remote-server-clear">
                    <Trash2 className="w-4 h-4 mr-1" /> Stop using it
                  </Button>
                </div>
              ) : (
                <>
                  <p>Run your <b>own</b> RustDesk server (free on Oracle Cloud) so your PCs never hit the public-server login wall or blocks. Set it up once — the app configures every PC for you.</p>
                  <div className="rounded-md bg-slate-50 border p-3 space-y-2">
                    <p className="font-semibold text-slate-800 text-[13px]">Easiest — auto-connect (no SSH)</p>
                    <ol className="list-decimal list-inside text-xs space-y-1">
                      <li>Sign up at <b>cloud.oracle.com</b> (free). Create → <b>Instance</b> → Ubuntu, shape <b>Always Free</b>.</li>
                      <li>Expand <b>Show advanced options → Management</b>, and paste the box below into <b>cloud-init script / user data</b>.</li>
                      <li>After creating, open the instance&apos;s <b>VCN → Security List</b> and add ingress rules: <b>TCP 21115–21119</b> and <b>UDP 21116</b> from <b>0.0.0.0/0</b>.</li>
                      <li>Wait ~2 minutes — this window turns green by itself when the server connects.</li>
                    </ol>
                    {cloudInit ? (
                      <>
                        <textarea readOnly value={cloudInit} className="w-full h-28 text-[10px] font-mono border rounded p-2 bg-white" data-testid="remote-server-cloudinit" onFocus={(e) => e.target.select()} />
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => copy(cloudInit, "cloudinit")} data-testid="remote-server-copy-cloudinit">
                            {copied === "cloudinit" ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Copy the setup script
                          </Button>
                          <span className="text-[11px] text-emerald-700 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Waiting for your server to connect…</span>
                        </div>
                      </>
                    ) : (
                      <Button size="sm" onClick={generateCloudInit} data-testid="remote-server-generate">
                        <Sparkles className="w-4 h-4 mr-1" /> Generate my server setup script
                      </Button>
                    )}
                  </div>
                  <div className="rounded-md bg-slate-50 border p-3 space-y-2">
                    <p className="font-semibold text-slate-800 text-[13px]">Already have a server? Enter it manually</p>
                    <input className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Server address / IP (e.g. 164.92.10.20)" value={srvForm.ip} onChange={(e) => setSrvForm((f) => ({ ...f, ip: e.target.value }))} data-testid="remote-server-ip" />
                    <input className="w-full border rounded px-2 py-1.5 text-sm font-mono" placeholder="Key (from id_ed25519.pub)" value={srvForm.key} onChange={(e) => setSrvForm((f) => ({ ...f, key: e.target.value }))} data-testid="remote-server-key" />
                    <Button size="sm" onClick={saveServerManual} data-testid="remote-server-save">Save server</Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Explicit install-client prompt. Connect never opens this automatically. */}
      {installPrompt && (
        <InstallRustDeskPrompt
          computer={installPrompt === "setup" ? null : installPrompt}
          server={server?.configured ? server : null}
          copy={copy}
          copied={copied}
          onConnectFix={downloadConnectFix}
          onWindowsInstall={downloadOperatorInstall}
          onRetry={() => { const c = installPrompt; setInstallPrompt(null); if (c && c !== "setup") setTimeout(() => connect(c), 150); }}
          onClose={() => setInstallPrompt(null)}
        />
      )}
    </div>
  );
}

// Shown when Connect couldn't launch RustDesk on THIS device. Detects the
// platform and auto-starts the right download (Windows .bat / Android APK /
// App Store / Mac DMG), with manual ID+password and private-server settings
// so phones and tablets can connect right after installing.
function InstallRustDeskPrompt({ computer, server, copy, copied, onConnectFix, onWindowsInstall, onRetry, onClose }) {
  const platform = getPlatform();
  const [started, setStarted] = useState(false);

  const startDownload = useCallback(() => {
    setStarted(true);
    if (platform === "windows") onWindowsInstall();
    else if (platform === "android") triggerFileDownload(RD_DL("android"));
    else if (platform === "ios") window.location.href = APP_STORE_URL;
    else if (platform === "mac") triggerFileDownload(RD_DL("mac"));
  }, [platform, onWindowsInstall]);

  // Auto-start the correct download shortly after the prompt opens.
  useEffect(() => {
    const t = setTimeout(startDownload, 700);
    return () => clearTimeout(t);
  }, [startDownload]);

  const mobile = platform === "android" || platform === "ios";
  return (
    <div className="absolute inset-0 z-[25] bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl max-h-[90%] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="remote-install-prompt">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Download className="w-5 h-5 text-indigo-600" />
          <span className="font-semibold">RustDesk isn&apos;t on this device yet</span>
          <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={onClose} data-testid="install-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 text-sm text-slate-600">
          <p>
            {computer ? (
              <>Nothing opened when you pressed <b>Connect</b> — RustDesk isn&apos;t installed here. </>
            ) : (
              <>Let&apos;s get RustDesk on this device so you can control your computers from it. </>
            )}
            You&apos;re on <b>{PLATFORM_LABEL[platform]}</b>, so{" "}
            {platform === "windows" && <>the <b>Install-RustDesk-on-this-PC</b> file is downloading now. Run it, then press Connect again.</>}
            {platform === "android" && <>the <b>RustDesk app (APK)</b> is downloading now. Open it to install (allow &quot;install from this source&quot; if asked), then press Connect again.</>}
            {platform === "ios" && <>we&apos;re opening the <b>App Store</b> so you can install the free RustDesk app, then come back and press Connect again.</>}
            {platform === "mac" && <>the <b>RustDesk Mac installer (.dmg)</b> is downloading now. Open it, drag RustDesk to Applications, then press Connect again.</>}
            {platform === "other" && <>grab RustDesk from rustdesk.com/download, install it, then press Connect again.</>}
          </p>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={startDownload} data-testid="install-download-again">
              <Download className="w-4 h-4 mr-1" /> {started ? "Download again" : "Download now"}
            </Button>
            {platform === "android" && (
              <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="install-play-store">Get it on Google Play instead</Button>
              </a>
            )}
            {platform === "ios" && (
              <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="install-app-store">Open the App Store</Button>
              </a>
            )}
            {platform === "mac" && (
              <Button size="sm" variant="outline" onClick={() => triggerFileDownload(RD_DL("mac-intel"))} data-testid="install-mac-intel">
                Intel Mac? Download that version
              </Button>
            )}
            {platform === "other" && (
              <a href={RUSTDESK_DOWNLOAD} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="install-generic-dl">Open rustdesk.com/download</Button>
              </a>
            )}
          </div>

          {!computer && mobile && (
            <div className="rounded-md bg-slate-50 border p-2.5 text-xs text-slate-600" data-testid="install-connect-anypc-hint">
              <b>Once it&apos;s installed:</b> come back here and press <b>Connect</b> on any computer&apos;s card — or open the RustDesk app and type that computer&apos;s <b>ID</b> and <b>password</b> (the key icon on each card shows them). Works for every PC in this list.
            </div>
          )}

          {mobile && computer && (
            <div className="rounded-lg border bg-slate-50 divide-y" data-testid="install-manual-creds">
              <div className="px-3 py-2 text-[11px] text-slate-500">After installing, connect to <b>{computer.name}</b> with:</div>
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="text-xs text-slate-400 w-20">ID</span>
                <span className="font-mono font-semibold text-slate-800 flex-1">{computer.rustdesk_id}</span>
                <button type="button" className="text-slate-400 hover:text-indigo-600" onClick={() => copy(computer.rustdesk_id, "iid")} data-testid="install-copy-id">
                  {copied === "iid" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              {computer.password && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="text-xs text-slate-400 w-20">Password</span>
                  <span className="font-mono font-semibold text-slate-800 flex-1 break-all">{computer.password}</span>
                  <button type="button" className="text-slate-400 hover:text-indigo-600" onClick={() => copy(computer.password, "ipw")} data-testid="install-copy-pw">
                    {copied === "ipw" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          )}

          {mobile && server && (() => { const cfg = buildServerConfigString(server); return (
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2.5 text-xs text-indigo-800" data-testid="install-server-settings">
              <b>Connect the app to your server</b> — after installing, tap this:
              <a href={`rustdesk://config/${cfg}`} className="block mt-1.5">
                <Button size="sm" className="w-full" data-testid="install-autoconfig">Set the server in RustDesk automatically</Button>
              </a>
              <p className="mt-1.5 text-[11px]">If nothing happens, enter it by hand in the app — <b>Settings → ID/Relay Server</b> (leave Relay/API empty):</p>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="text-indigo-500 w-16 shrink-0">ID server</span>
                <span className="font-mono font-semibold flex-1 break-all">{server.ip}</span>
                <button type="button" className="text-indigo-400 hover:text-indigo-700" onClick={() => copy(server.ip, "isrv")} data-testid="install-copy-srv-ip">
                  {copied === "isrv" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="text-indigo-500 w-16 shrink-0">Key</span>
                <span className="font-mono font-semibold flex-1 break-all">{server.key}</span>
                <button type="button" className="text-indigo-400 hover:text-indigo-700" onClick={() => copy(server.key, "ikey")} data-testid="install-copy-srv-key">
                  {copied === "ikey" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <details className="mt-1.5">
                <summary className="cursor-pointer text-indigo-600">Or copy the one-paste setup code</summary>
                <div className="mt-1.5 rounded border border-indigo-200 bg-white p-2 font-mono text-[10px] break-all select-all" data-testid="install-config-string">{cfg}</div>
                <Button size="sm" variant="outline" className="w-full mt-1.5" onClick={() => copy(cfg, "icfg")} data-testid="install-copy-config">
                  {copied === "icfg" ? <><Check className="w-4 h-4 mr-1" /> Copied — use the paste icon in ID/Relay Server</> : <><Copy className="w-4 h-4 mr-1" /> Copy setup code</>}
                </Button>
              </details>
            </div>
          ); })()}

          {platform === "windows" && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
              RustDesk <b>is</b> already installed on this PC? Then only the one-click link is broken — run this quick fix (asks for admin), then press Connect again:
              <Button size="sm" variant="outline" className="w-full mt-1.5" onClick={onConnectFix} data-testid="install-connect-fix">
                <Download className="w-4 h-4 mr-1" /> Download one-click Connect fix
              </Button>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="install-cancel">{computer ? "Close" : "Done"}</Button>
          {computer && (
            <Button onClick={onRetry} data-testid="install-retry-connect">
              <Play className="w-4 h-4 mr-1" /> I installed it — Connect again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
